<!-- Self-hosted cookie consent banner + preferences modal (CMP).
     Mounted globally from App.vue. Blocks nothing on its own — the actual tag
     gating lives in public/index.html (window.IcConsent) and src/consent/
     consentManager.js. This is the UI: privacy-by-default, equal-prominence
     accept/reject (Garante 10/06/2021), granular categories, revocation. -->
<template>
  <div v-if="consentState.open" class="ic-cc-root">
    <!-- Backdrop: preferences modal only. -->
    <div v-if="isPreferences" class="ic-cc-backdrop" @click="onBackdrop"></div>

    <!-- First visit / banner. -->
    <section
      v-if="!isPreferences"
      class="ic-cc-card ic-cc-banner"
      role="dialog"
      aria-modal="false"
      aria-labelledby="ic-cc-title"
      aria-describedby="ic-cc-desc"
    >
      <div class="ic-cc-banner__text">
        <h2 id="ic-cc-title" class="ic-cc-title">Rispettiamo la tua privacy</h2>
        <p id="ic-cc-desc" class="ic-cc-desc">
          Usiamo cookie tecnici, necessari al funzionamento del sito, e — solo con il tuo
          consenso — cookie di statistica e di profilazione per migliorare il servizio e
          mostrarti contenuti pertinenti. Puoi accettarli tutti, rifiutarli tutti o scegliere
          quali attivare. Maggiori dettagli nella
          <router-link class="ic-cc-link" to="/cookie-policy">Cookie Policy</router-link>.
        </p>
      </div>
      <div class="ic-cc-actions">
        <button type="button" class="ic-cc-btn ic-cc-btn--reject" @click="rejectAll">
          Rifiuta tutti
        </button>
        <button type="button" class="ic-cc-btn ic-cc-btn--accept" @click="acceptAll">
          Accetta tutti
        </button>
        <button type="button" class="ic-cc-linkbtn" @click="openPrefs">Personalizza</button>
      </div>
    </section>

    <!-- Customize / reopened from the footer. -->
    <section
      v-else
      ref="prefsCard"
      tabindex="-1"
      class="ic-cc-card ic-cc-prefs"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ic-cc-prefs-title"
    >
      <header class="ic-cc-prefs__head">
        <h2 id="ic-cc-prefs-title" class="ic-cc-title">Impostazioni cookie</h2>
        <button
          v-if="decided"
          type="button"
          class="ic-cc-close"
          aria-label="Chiudi"
          @click="close"
        >
          &times;
        </button>
      </header>
      <p class="ic-cc-desc">
        Gestisci le preferenze per categoria. I cookie tecnici sono sempre attivi perché
        indispensabili al funzionamento del sito. Per i dettagli consulta la
        <router-link class="ic-cc-link" to="/cookie-policy">Cookie Policy</router-link>.
      </p>

      <ul class="ic-cc-cats">
        <li class="ic-cc-cat">
          <div class="ic-cc-cat__info">
            <span class="ic-cc-cat__name">Necessari</span>
            <span class="ic-cc-cat__desc">
              Indispensabili per la navigazione e le funzioni di base. Sempre attivi.
            </span>
          </div>
          <label class="ic-cc-switch ic-cc-switch--locked" aria-label="Cookie necessari (sempre attivi)">
            <input type="checkbox" checked disabled />
            <span class="ic-cc-switch__slider"></span>
          </label>
        </li>

        <li class="ic-cc-cat">
          <div class="ic-cc-cat__info">
            <span class="ic-cc-cat__name">Statistici</span>
            <span class="ic-cc-cat__desc">
              Ci aiutano a capire come viene usato il sito, in forma aggregata
              (Google Analytics 4).
            </span>
          </div>
          <label class="ic-cc-switch" aria-label="Cookie statistici">
            <input type="checkbox" v-model="draft.analytics" />
            <span class="ic-cc-switch__slider"></span>
          </label>
        </li>

        <li class="ic-cc-cat">
          <div class="ic-cc-cat__info">
            <span class="ic-cc-cat__name">Marketing e profilazione</span>
            <span class="ic-cc-cat__desc">
              Usati per misurare le nostre campagne pubblicitarie e mostrarti annunci di
              ImmaginaCasa su altri siti (Google Ads, tramite Google Tag Manager).
            </span>
          </div>
          <label class="ic-cc-switch" aria-label="Cookie di marketing e profilazione">
            <input type="checkbox" v-model="draft.marketing" />
            <span class="ic-cc-switch__slider"></span>
          </label>
        </li>
      </ul>

      <div class="ic-cc-actions ic-cc-actions--prefs">
        <button type="button" class="ic-cc-btn ic-cc-btn--reject" @click="rejectAll">
          Rifiuta tutti
        </button>
        <button type="button" class="ic-cc-btn ic-cc-btn--accept" @click="acceptAll">
          Accetta tutti
        </button>
        <button type="button" class="ic-cc-btn ic-cc-btn--save" @click="saveDraft">
          Salva le mie scelte
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import {
  consentState,
  acceptAll,
  rejectAll,
  saveConsent,
  openPreferences,
  currentCategories,
  initConsentUi,
} from '@/consent/consentManager';

export default {
  name: 'CookieConsentBanner',
  data() {
    return {
      consentState,
      draft: { analytics: false, marketing: false },
    };
  },
  computed: {
    isPreferences() {
      return consentState.mode === 'preferences';
    },
    decided() {
      return consentState.decided;
    },
    modalOpen() {
      return consentState.open && this.isPreferences;
    },
  },
  watch: {
    'consentState.mode'(mode) {
      // Prefill the toggles from the stored choice whenever preferences opens.
      if (mode === 'preferences') {
        const c = currentCategories();
        this.draft.analytics = !!c.analytics;
        this.draft.marketing = !!c.marketing;
        this.focusDialog();
      }
    },
    modalOpen(locked) {
      // Lock background scroll while the modal is open.
      document.documentElement.style.overflow = locked ? 'hidden' : '';
    },
  },
  mounted() {
    initConsentUi();
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown);
    document.documentElement.style.overflow = '';
  },
  methods: {
    acceptAll,
    rejectAll,
    openPrefs: openPreferences,
    saveDraft() {
      saveConsent(
        { analytics: this.draft.analytics, marketing: this.draft.marketing },
        'custom'
      );
    },
    close() {
      // Only reachable when a decision already exists (reopened from the footer).
      consentState.open = false;
      consentState.mode = 'banner';
    },
    onBackdrop() {
      // Closing must never count as consent. With a prior decision we just hide;
      // on a first visit we fall back to the banner so a choice is still required.
      if (this.decided) this.close();
      else consentState.mode = 'banner';
    },
    onKeydown(e) {
      if (e.key !== 'Escape' || !this.isPreferences) return;
      if (this.decided) this.close();
      else consentState.mode = 'banner';
    },
    focusDialog() {
      this.$nextTick(() => {
        const el = this.$refs.prefsCard;
        if (el && typeof el.focus === 'function') el.focus();
      });
    },
  },
};
</script>
